import React from 'react';
import { colour, radius, space, text } from '../../design/tokens.ts';
import { Button, Status, Tag, Thumb } from '../../components/basics.tsx';
import type { BoardAnswer, BoardRow, BoardStep } from './types.ts';

/**
 * The results table: one row per VC pass, one column per step of **this
 * project's** pipeline.
 *
 * Nothing here knows the name of a step. `answer.columns` comes from the
 * project's own pipeline, in its order, so a project that adds a step gets a
 * column without a line of this changing.
 *
 * Each cell is one of three things: a result (version, owner, where it is), a
 * step that could be launched, or a step waiting on an earlier one. A stale
 * result says why it is stale, because "stale" without a reason is the thing
 * people come and ask about.
 */

function StepCell({
  step,
  stepKey,
  label,
  row,
  order,
  canLaunch,
  onLaunch,
  busy
}: {
  step: BoardStep | undefined;
  stepKey: string;
  label: string;
  row: BoardRow;
  /**
   * This project's step keys, in pipeline order. It has to come from the
   * pipeline rather than from `row.steps`, which holds only the steps that
   * have a result — so a pass with nothing made would have an empty order and
   * every cell would look launchable.
   */
  order: string[];
  canLaunch: boolean;
  onLaunch: (passId: string, stepKey: string) => void;
  busy: boolean;
}) {
  const state = step?.state ?? 'missing';

  if (row.state === 'blocked') {
    return (
      <Cell>
        <span style={{ ...text.note, color: colour.inkMuted }}>
          {row.waiting_for
            ? `waiting for ${row.waiting_for.vc_id} to be created${row.waiting_for.assigned_to ? ` by ${row.waiting_for.assigned_to}` : ''}`
            : 'blocked by the VC setup'}
        </span>
      </Cell>
    );
  }

  if (state === 'missing') {
    // The next step that could be run, or one waiting on an earlier one. The
    // difference is whether everything before it has a result.
    const here = order.indexOf(stepKey);
    const earlierMissing = order
      .slice(0, here)
      .some((key) => (row.steps[key]?.state ?? 'missing') === 'missing');
    if (earlierMissing) {
      return (
        <Cell>
          <span style={{ ...text.note, color: colour.inkMuted }}>waiting on an earlier step</span>
        </Cell>
      );
    }
    return (
      <Cell>
        <span style={{ ...text.note, color: colour.inkMuted }}>not made yet</span>
        {canLaunch && (
          <Button
            tone="plain"
            onClick={() => onLaunch(row._id, stepKey)}
            disabled={busy}
            style={{
              padding: '0 12px',
              borderColor: colour.accent,
              color: colour.link,
              font: "600 13px 'IBM Plex Sans', system-ui, sans-serif"
            }}
          >
            {busy ? 'Launching…' : `Launch ${label}`}
          </Button>
        )}
      </Cell>
    );
  }

  return (
    <Cell>
      <div style={{ display: 'flex', gap: space.tight, alignItems: 'flex-start' }}>
        <Thumb
          url={step?.thumbnail?.url}
          alt={`${label} for ${row.vc_id}`}
          size={64}
          {...(step?.thumbnail?.stale ? { stale: true } : {})}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <Status value={state === 'current' ? 'current' : state} />
          {step?.variant && <Tag>on {step.variant}</Tag>}
          {step?.owner && <span style={{ ...text.note, color: colour.inkSoft }}>{step.owner}</span>}
        </div>
      </div>
      {state === 'stale' && step?.stale_reason && (
        <span style={{ ...text.note, color: colour.staleInk }}>
          {step.accepted ? `kept as it is — ${step.stale_reason}` : step.stale_reason}
        </span>
      )}
    </Cell>
  );
}

function Cell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: space.control,
        borderBottom: `1px solid ${colour.edgeSoft}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 6,
        minWidth: 0
      }}
    >
      {children}
    </div>
  );
}

export function Rows({
  answer,
  canLaunch,
  onLaunch,
  launching,
  onOpenPass
}: {
  answer: BoardAnswer;
  canLaunch: boolean;
  onLaunch: (passId: string, stepKey: string) => void;
  launching: string | null;
  onOpenPass: (row: BoardRow) => void;
}) {
  const byVc = answer.view === 'by_vc';
  const groupOf = (row: BoardRow): string => (byVc ? row.vc_id : row.sequence_id);
  const groups = [...new Set(answer.rows.map(groupOf))];
  const nameOf = (key: string): string =>
    answer.groups.find((g) => g.key === key)?.label ?? key;

  const head: React.CSSProperties = {
    padding: '14px 12px',
    borderBottom: `1px solid ${colour.edge}`,
    ...text.note,
    fontWeight: 600,
    color: colour.inkMuted,
    position: 'sticky',
    top: 0,
    background: colour.surface,
    zIndex: 1
  };

  return (
    <div
      style={{
        overflowX: 'auto',
        background: colour.surface,
        border: `1px solid ${colour.edge}`,
        borderRadius: radius.card
      }}
    >
      <div
        style={{
          minWidth: 320 + answer.columns.length * 190,
          display: 'grid',
          gridTemplateColumns: `220px repeat(${answer.columns.length}, minmax(180px, 1fr)) 150px`
        }}
      >
        <div style={head}>{byVc ? 'Sequence' : 'Virtual camera'}</div>
        {answer.columns.map((column) => (
          <div key={column.key} style={head}>
            {column.label}
          </div>
        ))}
        <div style={head}>In edits</div>

        {groups.map((group) => {
          const rows = answer.rows.filter((r) => groupOf(r) === group);
          return (
            <GroupBlock
              key={group}
              title={nameOf(group)}
              count={rows.length}
              rows={rows}
              answer={answer}
              byVc={byVc}
              canLaunch={canLaunch}
              onLaunch={onLaunch}
              launching={launching}
              onOpenPass={onOpenPass}
            />
          );
        })}
      </div>
    </div>
  );
}

function GroupBlock({
  title,
  count,
  rows,
  answer,
  byVc,
  canLaunch,
  onLaunch,
  launching,
  onOpenPass
}: {
  title: string;
  count: number;
  rows: BoardRow[];
  answer: BoardAnswer;
  byVc: boolean;
  canLaunch: boolean;
  onLaunch: (passId: string, stepKey: string) => void;
  launching: string | null;
  onOpenPass: (row: BoardRow) => void;
}) {
  const states = rows.reduce<Record<string, number>>((held, row) => {
    held[row.state] = (held[row.state] ?? 0) + 1;
    return held;
  }, {});

  return (
    <>
      <div
        style={{
          gridColumn: '1 / -1',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: space.control,
          padding: '12px 16px',
          background: colour.ground,
          borderBottom: `1px solid ${colour.edgeSoft}`
        }}
      >
        <span style={text.h3}>{title}</span>
        <span style={{ ...text.small, color: colour.inkSoft }}>
          {count} {count === 1 ? 'pass' : 'passes'} here
        </span>
        {Object.entries(states).map(([state, n]) => (
          <Status key={state} value={state} detail={String(n)} />
        ))}
      </div>

      {rows.map((row) => (
        <React.Fragment key={row._id}>
          <div
            style={{
              padding: space.control,
              borderBottom: `1px solid ${colour.edgeSoft}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 6
            }}
          >
            <button
              type="button"
              onClick={() => onOpenPass(row)}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                textAlign: 'left',
                cursor: 'pointer',
                ...text.monoStrong,
                color: colour.link
              }}
            >
              {byVc ? (answer.groups.find((g) => g.key === row.sequence_id)?.label ?? 'sequence') : row.vc_id}
            </button>
            <Status value={row.state} />
            {row.state_reason && (
              <span style={{ ...text.note, color: colour.inkSoft }}>{row.state_reason}</span>
            )}
            {row.rendered_on && <Tag>rendered on {row.rendered_on}</Tag>}
          </div>

          {answer.columns.map((column) => (
            <StepCell
              key={column.key}
              step={row.steps[column.key]}
              stepKey={column.key}
              label={column.label}
              row={row}
              order={answer.columns.map((c) => c.key)}
              canLaunch={canLaunch}
              onLaunch={onLaunch}
              busy={launching === `${row._id}:${column.key}`}
            />
          ))}

          <Cell>
            {row.in_edits.length === 0 ? (
              <span style={{ ...text.note, color: colour.inkMuted }}>not in any edit</span>
            ) : (
              row.in_edits.map((entry) => (
                <span key={`${entry.edit_id}-${entry.movie_id}`}>
                  <Status
                    value={entry.current ? 'current' : 'stale'}
                    detail={entry.current ? 'in an edit' : 'older movie'}
                  />
                </span>
              ))
            )}
          </Cell>
        </React.Fragment>
      ))}
    </>
  );
}
